import { useEffect, useRef } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";

/**
 * How long a burst of arrivals is gathered before announcing once. A patient
 * joining publishes a microphone and a camera within milliseconds of each
 * other, and a reconnect republishes everything at once.
 */
export const ANNOUNCE_DEBOUNCE_MS = 750;

type Publication = { kind?: string };

/**
 * Announce the meeting again whenever anyone in it starts sending audio.
 *
 * A session recorded track by track records each voice as its own track, and a
 * track can only be recorded once it exists — a patient who joins after the
 * therapist, or rejoins after a dropped connection, brings a track the first
 * announcement never saw. The server decides from LiveKit what, if anything,
 * to record; this only says "look again". Mounted inside the room.
 */
export function useAnnounceAudioTracks(announce: () => void): void {
    const room = useRoomContext();
    const latest = useRef(announce);

    useEffect(() => {
        latest.current = announce;
    }, [announce]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;

        const onPublished = (publication: Publication) => {
            if (publication.kind !== Track.Kind.Audio) return;
            clearTimeout(timer);
            timer = setTimeout(() => latest.current(), ANNOUNCE_DEBOUNCE_MS);
        };

        room.on(RoomEvent.TrackPublished, onPublished);
        room.on(RoomEvent.LocalTrackPublished, onPublished);
        return () => {
            clearTimeout(timer);
            room.off(RoomEvent.TrackPublished, onPublished);
            room.off(RoomEvent.LocalTrackPublished, onPublished);
        };
    }, [room]);
}
